import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, RotateCcw, Star } from "lucide-react";
import { toast } from "sonner";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

import AdminNav from "@/components/AdminNav";
import IconPicker from "@/components/admin/IconPicker";
import ImageUploadField from "@/components/admin/ImageUploadField";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { useContent } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- generic primitives ---------------------------------------------------
function Field({ label, hint, children }) {
  return (
    <div>
      <label className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </div>
  );
}
const inputCls =
  "w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition";
const textareaCls = inputCls + " resize-y min-h-[90px]";

function TextField({ label, value, onChange, placeholder, hint, dataTestid }) {
  return (
    <Field label={label} hint={hint}>
      <input
        type="text"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
        data-testid={dataTestid}
      />
    </Field>
  );
}
function TextArea({ label, value, onChange, placeholder, hint, dataTestid, rows = 3 }) {
  return (
    <Field label={label} hint={hint}>
      <textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={textareaCls} data-testid={dataTestid} />
    </Field>
  );
}

// ---- generic editable list (with order arrows + add/remove) --------------
function ListEditor({ items = [], onChange, renderItem, newItem, addLabel = "Add item", testidPrefix }) {
  const move = (i, dir) => {
    const next = [...items];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const updateAt = (i, v) => {
    const next = [...items];
    next[i] = typeof v === "function" ? v(next[i]) : v;
    onChange(next);
  };
  const removeAt = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () => onChange([...items, typeof newItem === "function" ? newItem() : newItem]);

  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} data-testid={testidPrefix && `${testidPrefix}-${i}`} className="bg-[#f8fafc] rounded-2xl p-4 border border-slate-100 relative">
          <div className="flex justify-between items-start gap-2 mb-3">
            <span className="text-xs font-mono text-slate-400">#{i + 1}</span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="w-7 h-7 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="w-7 h-7 rounded-full bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button type="button" onClick={() => removeAt(i)} className="w-7 h-7 rounded-full bg-red-50 text-red-600 hover:bg-red-600 hover:text-white flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          {renderItem(it, (v) => updateAt(i, v), i)}
        </div>
      ))}
      <button type="button" onClick={add} className="w-full inline-flex items-center justify-center gap-2 bg-white border-2 border-dashed border-slate-300 hover:border-[#002570] hover:text-[#002570] text-slate-500 rounded-2xl px-5 py-3 text-sm font-medium transition-colors">
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}

// ---- main page -----------------------------------------------------------
export default function AdminContent() {
  const { user, authHeader } = useAuth();
  const { content: serverContent, refetch } = useContent();
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  useEffect(() => {
    if (serverContent && Object.keys(serverContent).length) {
      setDraft(JSON.parse(JSON.stringify(serverContent)));
    }
  }, [serverContent]);

  const set = (path, value) => {
    setDraft((d) => {
      const next = JSON.parse(JSON.stringify(d || {}));
      let cur = next;
      const parts = path.split(".");
      for (let i = 0; i < parts.length - 1; i++) {
        if (cur[parts[i]] === undefined || cur[parts[i]] === null) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };
  const get = (path, fallback) => {
    if (!draft) return fallback;
    return path.split(".").reduce((o, k) => (o == null ? o : o[k]), draft) ?? fallback;
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    try {
      await axios.put(`${API}/content`, draft, { headers: authHeader });
      toast.success("Site content saved. Changes are live!");
      refetch();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/content/reset`, {}, { headers: authHeader });
      toast.success("Restored defaults");
      refetch();
      setResetOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  if (user === undefined || draft === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <Loader2 className="w-8 h-8 animate-spin text-[#002570]" />
      </div>
    );
  }
  if (user === null) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-24">
      <AdminNav />

      <main className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Admin Dashboard</p>
            <h1 className="mt-2 font-rubik text-3xl md:text-4xl font-bold text-[#0f172a]">Site Content</h1>
            <p className="mt-2 font-inter text-sm text-slate-600">Edit every word, photo and number on the public website.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setResetOpen(true)} data-testid="content-reset-btn" className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-red-600 rounded-full hover:bg-red-50 transition-colors">
              <RotateCcw className="w-4 h-4" /> Reset to Defaults
            </button>
          </div>
        </div>

        <Tabs defaultValue="hero" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto bg-white border border-slate-200 rounded-2xl p-1 gap-1" data-testid="content-tabs">
            {[
              ["hero", "Hero"],
              ["stats", "Stats"],
              ["usps", "USPs"],
              ["video", "Video Tour"],
              ["guidance", "Guidance"],
              ["partnership", "Partnership"],
              ["stories", "Stories"],
              ["fees", "Fees"],
              ["courses", "Courses"],
              ["contact", "Contact"],
              ["footer", "Footer"],
              ["social", "Social"],
              ["marquee", "Marquee"],
            ].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`content-tab-${v}`} className="data-[state=active]:bg-[#002570] data-[state=active]:text-white rounded-xl px-4 py-2 text-sm font-inter">{l}</TabsTrigger>
            ))}
          </TabsList>

          {/* ========= HERO ========= */}
          <TabsContent value="hero" className="space-y-5">
            <Section title="Hero Section">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow text" value={get("hero.eyebrow")} onChange={(v) => set("hero.eyebrow", v)} dataTestid="hero-eyebrow" />
                <TextField label="Subhead" value={get("hero.subhead")} onChange={(v) => set("hero.subhead", v)} />
                <TextField label="Headline (start)" value={get("hero.headline_pre")} onChange={(v) => set("hero.headline_pre", v)} dataTestid="hero-headline-pre" />
                <TextField label="Headline (emphasis – blue)" value={get("hero.headline_emphasis")} onChange={(v) => set("hero.headline_emphasis", v)} />
                <TextField label="Headline (end)" value={get("hero.headline_post")} onChange={(v) => set("hero.headline_post", v)} />
                <TextField label="Photo badge — count" value={get("hero.badge_count")} onChange={(v) => set("hero.badge_count", v)} placeholder="500+" />
                <TextField label="Photo badge — label" value={get("hero.badge_label")} onChange={(v) => set("hero.badge_label", v)} placeholder="Students Mentored" />
                <TextField label="Photo badge — sublabel" value={get("hero.badge_sublabel")} onChange={(v) => set("hero.badge_sublabel", v)} />
              </div>
              <ImageUploadField label="Hero Image" value={get("hero.image_url")} onChange={(v) => set("hero.image_url", v)} hint="Recommended portrait orientation. Leave blank to use default team photo." dataTestid="hero-image-upload" />
              <Field label="Trust badges (one per line)">
                <textarea
                  rows={3}
                  value={(get("hero.trust_badges", []) || []).join("\n")}
                  onChange={(e) => set("hero.trust_badges", e.target.value.split("\n").filter((s) => s.trim()))}
                  className={textareaCls}
                />
              </Field>
            </Section>
          </TabsContent>

          {/* ========= STATS ========= */}
          <TabsContent value="stats" className="space-y-5">
            <Section title="Stat Counters" subtitle="Numbers roll on scroll. Use suffix like '+', '%' etc.">
              <ListEditor
                items={get("stats", [])}
                onChange={(v) => set("stats", v)}
                addLabel="Add a stat"
                testidPrefix="stat-row"
                newItem={{ num: "0", suffix: "+", label: "" }}
                renderItem={(it, upd) => (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <TextField label="Number" value={it.num} onChange={(v) => upd({ ...it, num: v })} placeholder="500" />
                    <TextField label="Suffix" value={it.suffix} onChange={(v) => upd({ ...it, suffix: v })} placeholder="+" />
                    <TextField label="Label" value={it.label} onChange={(v) => upd({ ...it, label: v })} placeholder="Students Guided" />
                  </div>
                )}
              />
            </Section>
          </TabsContent>

          {/* ========= USPs ========= */}
          <TabsContent value="usps" className="space-y-5">
            <Section title="USP / Why Choose Us">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow" value={get("usps_eyebrow")} onChange={(v) => set("usps_eyebrow", v)} />
                <TextField label="Headline (start)" value={get("usps_headline_pre")} onChange={(v) => set("usps_headline_pre", v)} />
                <TextField label="Headline (emphasis)" value={get("usps_headline_emphasis")} onChange={(v) => set("usps_headline_emphasis", v)} />
                <TextField label="Subtitle" value={get("usps_subtitle")} onChange={(v) => set("usps_subtitle", v)} />
              </div>
              <ListEditor
                items={get("usps", [])}
                onChange={(v) => set("usps", v)}
                addLabel="Add a USP"
                testidPrefix="usp-row"
                newItem={{ icon: "Star", title: "", desc: "" }}
                renderItem={(it, upd) => (
                  <div className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-3"><IconPicker value={it.icon} onChange={(v) => upd({ ...it, icon: v })} /></div>
                    <div className="md:col-span-3"><TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} /></div>
                    <div className="md:col-span-6"><TextField label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} /></div>
                  </div>
                )}
              />
            </Section>
          </TabsContent>

          {/* ========= VIDEO TOUR ========= */}
          <TabsContent value="video" className="space-y-5">
            <Section title="Video Tour">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow" value={get("video_tour.eyebrow")} onChange={(v) => set("video_tour.eyebrow", v)} />
                <TextField label="Headline (start)" value={get("video_tour.headline_pre")} onChange={(v) => set("video_tour.headline_pre", v)} />
                <TextField label="Headline (emphasis)" value={get("video_tour.headline_emphasis")} onChange={(v) => set("video_tour.headline_emphasis", v)} />
                <TextField label="Headline (end)" value={get("video_tour.headline_post")} onChange={(v) => set("video_tour.headline_post", v)} />
                <TextField label="Video overlay title" value={get("video_tour.title")} onChange={(v) => set("video_tour.title", v)} />
                <TextField label="Video overlay subtitle" value={get("video_tour.subtitle")} onChange={(v) => set("video_tour.subtitle", v)} />
              </div>
              <TextArea label="Body" value={get("video_tour.body")} onChange={(v) => set("video_tour.body", v)} />
              <Field label="Bullets (one per line)">
                <textarea
                  rows={3}
                  value={(get("video_tour.bullets", []) || []).join("\n")}
                  onChange={(e) => set("video_tour.bullets", e.target.value.split("\n").filter((s) => s.trim()))}
                  className={textareaCls}
                />
              </Field>
              <TextField label="Video URL" value={get("video_tour.video_url")} onChange={(v) => set("video_tour.video_url", v)} placeholder="https://… mp4 URL" />
              <ImageUploadField label="Video Poster (fallback while video loads)" value={get("video_tour.poster_url")} onChange={(v) => set("video_tour.poster_url", v)} />
            </Section>
          </TabsContent>

          {/* ========= GUIDANCE ========= */}
          <TabsContent value="guidance" className="space-y-5">
            <Section title="Career Guidance">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow" value={get("career_guidance.eyebrow")} onChange={(v) => set("career_guidance.eyebrow", v)} />
                <TextField label="Headline (start)" value={get("career_guidance.headline_pre")} onChange={(v) => set("career_guidance.headline_pre", v)} />
                <TextField label="Headline (emphasis)" value={get("career_guidance.headline_emphasis")} onChange={(v) => set("career_guidance.headline_emphasis", v)} />
                <TextField label="Headline (end)" value={get("career_guidance.headline_post")} onChange={(v) => set("career_guidance.headline_post", v)} />
                <TextField label="Image caption — title" value={get("career_guidance.image_caption_title")} onChange={(v) => set("career_guidance.image_caption_title", v)} />
                <TextField label="Image caption — sub" value={get("career_guidance.image_caption_sub")} onChange={(v) => set("career_guidance.image_caption_sub", v)} />
              </div>
              <ImageUploadField label="Section Image" value={get("career_guidance.image_url")} onChange={(v) => set("career_guidance.image_url", v)} />
              <ListEditor
                items={get("career_guidance.items", [])}
                onChange={(v) => set("career_guidance.items", v)}
                addLabel="Add guidance item"
                newItem={{ icon: "Compass", title: "", desc: "" }}
                renderItem={(it, upd) => (
                  <div className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-3"><IconPicker value={it.icon} onChange={(v) => upd({ ...it, icon: v })} /></div>
                    <div className="md:col-span-3"><TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} /></div>
                    <div className="md:col-span-6"><TextField label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} /></div>
                  </div>
                )}
              />
            </Section>
          </TabsContent>

          {/* ========= PARTNERSHIP ========= */}
          <TabsContent value="partnership" className="space-y-5">
            <Section title="College Partnership">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow" value={get("partnership.eyebrow")} onChange={(v) => set("partnership.eyebrow", v)} />
                <TextField label="Headline (start)" value={get("partnership.headline_pre")} onChange={(v) => set("partnership.headline_pre", v)} />
                <TextField label="Headline (emphasis)" value={get("partnership.headline_emphasis")} onChange={(v) => set("partnership.headline_emphasis", v)} />
                <TextField label="Partner WhatsApp number (digits only)" value={get("partnership.whatsapp_raw")} onChange={(v) => set("partnership.whatsapp_raw", v)} placeholder="917907645014" hint="Used by the 'Partner With Us' button. Defaults to main WhatsApp if blank." />
              </div>
              <TextArea label="Body" value={get("partnership.body")} onChange={(v) => set("partnership.body", v)} />
              <ListEditor
                items={get("partnership.benefits", [])}
                onChange={(v) => set("partnership.benefits", v)}
                addLabel="Add a benefit"
                newItem={{ icon: "Building2", title: "", desc: "" }}
                renderItem={(it, upd) => (
                  <div className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-3"><IconPicker value={it.icon} onChange={(v) => upd({ ...it, icon: v })} /></div>
                    <div className="md:col-span-3"><TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} /></div>
                    <div className="md:col-span-6"><TextField label="Description" value={it.desc} onChange={(v) => upd({ ...it, desc: v })} /></div>
                  </div>
                )}
              />
            </Section>
          </TabsContent>

          {/* ========= STORIES ========= */}
          <TabsContent value="stories" className="space-y-5">
            <Section title="Success Stories / Testimonials">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow" value={get("placements.eyebrow")} onChange={(v) => set("placements.eyebrow", v)} />
                <TextField label="Headline (start)" value={get("placements.headline_pre")} onChange={(v) => set("placements.headline_pre", v)} />
                <TextField label="Headline (emphasis)" value={get("placements.headline_emphasis")} onChange={(v) => set("placements.headline_emphasis", v)} />
                <TextField label="Body" value={get("placements.body")} onChange={(v) => set("placements.body", v)} />
              </div>
              <ImageUploadField label="Background Image" value={get("placements.bg_image_url")} onChange={(v) => set("placements.bg_image_url", v)} />
              <ListEditor
                items={get("placements.stories", [])}
                onChange={(v) => set("placements.stories", v)}
                addLabel="Add a story"
                newItem={{ name: "", course: "", company: "", rating: 5, quote: "" }}
                renderItem={(it, upd) => (
                  <div className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-4"><TextField label="Name" value={it.name} onChange={(v) => upd({ ...it, name: v })} /></div>
                    <div className="md:col-span-4"><TextField label="Course" value={it.course} onChange={(v) => upd({ ...it, course: v })} /></div>
                    <div className="md:col-span-3"><TextField label="Company / Role" value={it.company} onChange={(v) => upd({ ...it, company: v })} /></div>
                    <div className="md:col-span-1">
                      <Field label="Stars">
                        <select value={it.rating} onChange={(e) => upd({ ...it, rating: Number(e.target.value) })} className={inputCls}>
                          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </Field>
                    </div>
                    <div className="md:col-span-12"><TextArea label="Quote" rows={3} value={it.quote} onChange={(v) => upd({ ...it, quote: v })} /></div>
                  </div>
                )}
              />
            </Section>
          </TabsContent>

          {/* ========= FEES ========= */}
          <TabsContent value="fees" className="space-y-5">
            <Section title="Fees & EMI">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Eyebrow" value={get("fees.eyebrow")} onChange={(v) => set("fees.eyebrow", v)} />
                <TextField label="Subtitle" value={get("fees.subtitle")} onChange={(v) => set("fees.subtitle", v)} />
                <TextField label="Headline (start)" value={get("fees.headline_pre")} onChange={(v) => set("fees.headline_pre", v)} />
                <TextField label="Headline (emphasis)" value={get("fees.headline_emphasis")} onChange={(v) => set("fees.headline_emphasis", v)} />
                <TextField label="Headline (end)" value={get("fees.headline_post")} onChange={(v) => set("fees.headline_post", v)} />
              </div>
              <ListEditor
                items={get("fees.plans", [])}
                onChange={(v) => set("fees.plans", v)}
                addLabel="Add a plan"
                newItem={{ icon: "Wallet", title: "", badge: "", perks: [] }}
                renderItem={(it, upd) => (
                  <div className="grid md:grid-cols-12 gap-3">
                    <div className="md:col-span-3"><IconPicker value={it.icon} onChange={(v) => upd({ ...it, icon: v })} /></div>
                    <div className="md:col-span-5"><TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} /></div>
                    <div className="md:col-span-4"><TextField label="Badge" value={it.badge} onChange={(v) => upd({ ...it, badge: v })} placeholder="Most Popular" /></div>
                    <div className="md:col-span-12">
                      <Field label="Perks (one per line)">
                        <textarea rows={3} value={(it.perks || []).join("\n")} onChange={(e) => upd({ ...it, perks: e.target.value.split("\n").filter((s) => s.trim()) })} className={textareaCls} />
                      </Field>
                    </div>
                  </div>
                )}
              />
            </Section>
          </TabsContent>

          {/* ========= COURSES ========= */}
          <TabsContent value="courses" className="space-y-5">
            {Object.keys(draft.courses || {}).map((slug) => (
              <CourseEditor key={slug} slug={slug} get={get} set={set} />
            ))}
          </TabsContent>

          {/* ========= CONTACT ========= */}
          <TabsContent value="contact" className="space-y-5">
            <Section title="Contact Details (used everywhere on the site)">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Phone (display)" value={get("contact.phone")} onChange={(v) => set("contact.phone", v)} placeholder="+91 62822 66137" dataTestid="contact-phone-input" />
                <TextField label="Phone (raw, for tel: links)" value={get("contact.phone_raw")} onChange={(v) => set("contact.phone_raw", v)} placeholder="+916282266137" />
                <TextField label="WhatsApp number (digits only)" value={get("contact.whatsapp_raw")} onChange={(v) => set("contact.whatsapp_raw", v)} placeholder="916282266137" hint="Used in WhatsApp deep links and the floating WhatsApp button." />
                <TextField label="Email" value={get("contact.email")} onChange={(v) => set("contact.email", v)} />
                <div className="md:col-span-2"><TextArea label="Address" value={get("contact.address")} onChange={(v) => set("contact.address", v)} rows={2} /></div>
                <div className="md:col-span-2"><TextField label="Positioning line" value={get("contact.positioning")} onChange={(v) => set("contact.positioning", v)} hint="The italic tagline shown in the footer." /></div>
              </div>
            </Section>
          </TabsContent>

          {/* ========= FOOTER ========= */}
          <TabsContent value="footer" className="space-y-5">
            <Section title="Footer">
              <TextArea label="Tagline" value={get("footer.tagline")} onChange={(v) => set("footer.tagline", v)} />
              <TextField label="Right credit line" value={get("footer.credit")} onChange={(v) => set("footer.credit", v)} />
            </Section>
          </TabsContent>

          {/* ========= SOCIAL ========= */}
          <TabsContent value="social" className="space-y-5">
            <Section title="Social Media Links" subtitle="Shown as icons in the footer. Leave blank to hide that icon.">
              <div className="grid md:grid-cols-2 gap-4">
                <TextField label="Facebook URL" value={get("social.facebook")} onChange={(v) => set("social.facebook", v)} placeholder="https://www.facebook.com/…" dataTestid="social-facebook-input" />
                <TextField label="Instagram URL" value={get("social.instagram")} onChange={(v) => set("social.instagram", v)} placeholder="https://www.instagram.com/…" dataTestid="social-instagram-input" />
                <TextField label="LinkedIn URL" value={get("social.linkedin")} onChange={(v) => set("social.linkedin", v)} placeholder="https://www.linkedin.com/company/…" dataTestid="social-linkedin-input" />
                <TextField label="YouTube URL" value={get("social.youtube")} onChange={(v) => set("social.youtube", v)} placeholder="https://youtube.com/@…" dataTestid="social-youtube-input" />
              </div>
            </Section>
          </TabsContent>

          {/* ========= MARQUEE ========= */}
          <TabsContent value="marquee" className="space-y-5">
            <Section title="Scrolling Marquee Items">
              <Field label="Items (one per line)" hint="These scroll across the blue band below the hero.">
                <textarea
                  rows={8}
                  value={(get("marquee_items", []) || []).join("\n")}
                  onChange={(e) => set("marquee_items", e.target.value.split("\n").filter((s) => s.trim()))}
                  className={textareaCls}
                />
              </Field>
            </Section>
          </TabsContent>
        </Tabs>
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-40 bg-[#002570] text-white rounded-full shadow-2xl px-3 py-2 flex items-center gap-2 border border-[#73c3ff]/30">
        <Star className="w-4 h-4 text-[#73c3ff] ml-2" />
        <span className="text-sm font-medium hidden sm:inline">Don't forget to save</span>
        <button
          onClick={save}
          disabled={saving}
          data-testid="content-save-btn"
          className="inline-flex items-center gap-2 bg-[#73c3ff] text-[#002570] hover:bg-white rounded-full px-5 py-2 text-sm font-semibold transition-colors disabled:opacity-70"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      {resetOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-5">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="font-rubik text-xl font-bold text-[#0f172a]">Reset to defaults?</h3>
            <p className="mt-2 font-inter text-sm text-slate-600">All your edits will be replaced with the original site content. This cannot be undone.</p>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setResetOpen(false)} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-full">Cancel</button>
              <button onClick={reset} disabled={saving} data-testid="content-reset-confirm-btn" className="inline-flex items-center gap-2 bg-red-600 text-white hover:bg-red-700 rounded-full px-5 py-2.5 text-sm font-medium">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />} Reset Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-sm space-y-5">
      <div>
        <h2 className="font-rubik text-xl md:text-2xl font-bold text-[#0f172a]">{title}</h2>
        {subtitle && <p className="mt-1 font-inter text-sm text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function CourseEditor({ slug, get, set }) {
  const base = `courses.${slug}`;
  const course = get(base, {});
  return (
    <Section title={`${course.name || slug.toUpperCase()} — ${course.full_name || ""}`}>
      <div className="grid md:grid-cols-2 gap-4">
        <TextField label="Display name" value={course.name} onChange={(v) => set(`${base}.name`, v)} />
        <TextField label="Full name" value={course.full_name} onChange={(v) => set(`${base}.full_name`, v)} />
        <TextField label="Issuing body" value={course.issuer} onChange={(v) => set(`${base}.issuer`, v)} />
        <TextField label="Tagline" value={course.tagline} onChange={(v) => set(`${base}.tagline`, v)} />
        <TextField label="Duration" value={course.duration} onChange={(v) => set(`${base}.duration`, v)} />
        <TextField label="Salary summary" value={course.salary_summary} onChange={(v) => set(`${base}.salary_summary`, v)} />
        <div><IconPicker value={course.icon} onChange={(v) => set(`${base}.icon`, v)} label="Card icon" /></div>
        <TextField label="SEO meta title" value={course.meta_title} onChange={(v) => set(`${base}.meta_title`, v)} />
        <div className="md:col-span-2"><TextArea label="SEO meta description" value={course.meta_description} onChange={(v) => set(`${base}.meta_description`, v)} /></div>
      </div>

      <Field label="Eligibility (one per line)">
        <textarea rows={3} value={(course.eligibility || []).join("\n")} onChange={(e) => set(`${base}.eligibility`, e.target.value.split("\n").filter((s) => s.trim()))} className={textareaCls} />
      </Field>
      <Field label="Why this course (one per line)">
        <textarea rows={3} value={(course.why || []).join("\n")} onChange={(e) => set(`${base}.why`, e.target.value.split("\n").filter((s) => s.trim()))} className={textareaCls} />
      </Field>

      <div>
        <p className="font-inter text-sm font-semibold text-slate-700 mb-2">Levels</p>
        <ListEditor
          items={course.levels || []}
          onChange={(v) => set(`${base}.levels`, v)}
          addLabel="Add a level"
          newItem={{ name: "", papers: [] }}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <TextField label="Level name" value={it.name} onChange={(v) => upd({ ...it, name: v })} />
              <Field label="Papers (one per line)">
                <textarea rows={3} value={(it.papers || []).join("\n")} onChange={(e) => upd({ ...it, papers: e.target.value.split("\n").filter((s) => s.trim()) })} className={textareaCls} />
              </Field>
            </div>
          )}
        />
      </div>

      <div>
        <p className="font-inter text-sm font-semibold text-slate-700 mb-2">Career paths</p>
        <ListEditor
          items={course.careers || []}
          onChange={(v) => set(`${base}.careers`, v)}
          addLabel="Add a career"
          newItem={{ title: "", salary: "" }}
          renderItem={(it, upd) => (
            <div className="grid md:grid-cols-2 gap-3">
              <TextField label="Title" value={it.title} onChange={(v) => upd({ ...it, title: v })} />
              <TextField label="Salary range" value={it.salary} onChange={(v) => upd({ ...it, salary: v })} />
            </div>
          )}
        />
      </div>

      <div>
        <p className="font-inter text-sm font-semibold text-slate-700 mb-2">FAQs</p>
        <ListEditor
          items={course.faqs || []}
          onChange={(v) => set(`${base}.faqs`, v)}
          addLabel="Add an FAQ"
          newItem={{ q: "", a: "" }}
          renderItem={(it, upd) => (
            <div className="space-y-3">
              <TextField label="Question" value={it.q} onChange={(v) => upd({ ...it, q: v })} />
              <TextArea label="Answer" rows={3} value={it.a} onChange={(v) => upd({ ...it, a: v })} />
            </div>
          )}
        />
      </div>
    </Section>
  );
}